import { Add } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import CustomButton from '../../../../../../../../../lib/designSystem/Button';
import { formatMoney } from '../../../../../../../../../lib/functions/utils';
import { ExtensionDocument } from '../../types';

interface ExtensionSummaryProps {
  extensions: ExtensionDocument[];
  setOpenDocumentSelector: (open: boolean) => void;
  noIvaComision?: number | null;
}

const ExtensionSummary = (props: ExtensionSummaryProps) => {
  const { extensions, setOpenDocumentSelector, noIvaComision } = props;

  const totalAmount = extensions.reduce(
    (sum, extension) => sum + extension.amount,
    0,
  );
  const totalRemainingAmount = extensions.reduce(
    (sum, extension) => sum + extension.documentRemainingAmountNet,
    0,
  );
  const totalRateCost = extensions.reduce(
    (sum, extension) => sum + extension.rateCost,
    0,
  );
  const totalOperationCost =
    extensions.reduce((sum, extension) => {
      const fixed = Number(extension.operationCostFixed) || 0;
      const variable = Number(extension.operationCostVariable) || 0;
      return sum + fixed + variable;
    }, 0) * (noIvaComision ? 1 : 1.19);

  const totalArrear = extensions.reduce(
    (sum, extension) => sum + (extension.totalArrear || 0),
    0,
  );

  const totalGeneral = totalOperationCost + totalRateCost;

  return (
    <Card style={{ marginBottom: 20, backgroundColor: '#f8f9fa' }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" fontWeight="bold">
                Resumen de Prórrogas
              </Typography>
              <Chip
                label={`${extensions.length} documento${extensions.length !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <CustomButton
                onClick={() => setOpenDocumentSelector(true)}
                color="primary"
                startIcon={<Add />}
                variant="outlined"
              >
                Agregar Documentos
              </CustomButton>
            </Box>
          </Grid>
        </Grid>

        {extensions.length > 0 && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Box
                    sx={{
                      py: 0.5,
                      textAlign: 'center',
                      flex: 1,
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Monto Total Documentos
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {formatMoney(totalAmount)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      py: 0.5,
                      textAlign: 'center',
                      flex: 1,
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Saldo Deuda
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="error">
                      {formatMoney(totalRemainingAmount)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      py: 0.5,
                      textAlign: 'center',
                      flex: 1,
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Total Atrasos
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="warning.main"
                    >
                      {formatMoney(totalArrear)}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={0.5}>
                <Box
                  sx={{
                    height: '100%',
                    borderLeft: '1px solid #e0e0e0',
                    display: { xs: 'none', md: 'block' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6.5}>
                <Box
                  sx={{
                    p: 0.5,
                    mt: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack spacing={0.5}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="textSecondary">
                        Costo Tasa
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formatMoney(totalRateCost)}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="textSecondary">
                        Costo Operación {noIvaComision ? '' : '+ IVA'}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formatMoney(totalOperationCost)}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body1" fontWeight="bold">
                        Total General
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {formatMoney(totalGeneral)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ExtensionSummary;
